import { ObjectTypes } from '@diligentcorp/rc-service-object-mapping';
import { useApi } from '@issuemanager/api-client';
import {
  ExtendedAxiosError,
  useAppConfig,
} from '@issuemanager/apps/client/hooks';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface RelationshipTypeData {
  value: string;
  label: string;
}

export function useRelationshipTypes(): {
  types: RelationshipTypeData[];
  isLoading: boolean;
  error: ExtendedAxiosError | null;
} {
  const { t } = useTranslation();
  const api = useApi();
  const appConfig = useAppConfig();
  const isIssueRelationshipsEnabled = useFeatureFlags(
    'issueRelationships',
  ) as boolean;

  const {
    data: relationshipTypes = [],
    isLoading,
    error,
  }: UseQueryResult<readonly string[], ExtendedAxiosError> = useQuery({
    queryKey: ['relationship-types'],
    queryFn: async () => {
      if (appConfig.isLocalDev) {
        return [ObjectTypes.EVIDENCE];
      }

      const response = await api.relationshipsApi.getRelationshipTypes();
      return response.data;
    },
    enabled: isIssueRelationshipsEnabled,
  });

  const types: RelationshipTypeData[] = useMemo(
    () =>
      relationshipTypes.map((type) => ({
        value: type,
        label: t(`relationshipTypes.${type}`, type),
      })),
    [relationshipTypes, t],
  );

  return {
    types,
    isLoading,
    error,
  };
}
